import React from 'react';
import { useParams } from 'react-router-dom';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

import { registerField } from 'core/forms';
import { Button } from 'uikit/buttons';
import { Skeleton } from 'uikit/loaders';
import { Typo } from 'uikit/content';

import styles from '../styles/formFieldReplaceVideoContainer.module.scss';
import VideoPlayer from '../components/videoPlayer';
import useReplaceVideo from '../hooks/useReplaceVideo';
import getReplaceVideoTitleKey from '../helpers/getReplaceVideoTitleKey';

import useVideoVersion from '../hooks/useVideoVersion';

/* eslint-disable camelcase */
const FormFieldReplaceVideoContainer = ({ schema }) => {
  const { videoProperties, videoPropertiesKey } = schema;
  const queryClient = useQueryClient();
  const { videoVersion, videoVersionKey } = useVideoVersion({ isEnabled: false });
  const params = { params: useParams(), queryClient, videoVersionKey, videoVersion };
  const { type } = params.params;

  const { video, video_file_name } =
    (type === 'on-demand' ? videoProperties.data?.video : videoProperties.data?.video_trailer) ||
    {};

  const updateCache = (data) => {
    const updateKey = type === 'on-demand' ? 'video' : 'video_trailer';

    queryClient.setQueryData(videoPropertiesKey, (oldProperties) => ({
      ...oldProperties,
      [updateKey]: {
        video: data.video,
        video_file_name: data.video_file_name,
        ...(data.video_source && { video_source: data.video_source }),
      },
    }));
  };

  const { isReplacing, onReplaceVideoClick } = useReplaceVideo({ ...params, updateCache });

  return (
    <div className={styles.element}>
      {isReplacing ? (
        <Skeleton height={170} width={300} />
      ) : (
        video && (
          <>
            <VideoPlayer
              className={styles.video}
              video={video}
              shouldNotFloat
              shouldHideMenu
              shouldHideCustomControls
              shouldNotAutoplay
              shouldNotEnroll
            />
            {video_file_name && <Typo variant="p">{video_file_name}</Typo>}
          </>
        )
      )}
      <Button
        text={i18n.t(getReplaceVideoTitleKey({ type, isReplacing: !!video }))}
        variant="outlined"
        onClick={() => onReplaceVideoClick(getReplaceVideoTitleKey({ type, isReplacing: !!video }))}
        isDisabled={isReplacing || schema.isDisabled}
      />
    </div>
  );
};

FormFieldReplaceVideoContainer.propTypes = {
  schema: PropTypes.shape({
    isDisabled: PropTypes.bool,
  }).isRequired,
};

const WrappedFormFieldReplaceVideo = React.memo(FormFieldReplaceVideoContainer);

registerField('ReplaceVideo', WrappedFormFieldReplaceVideo);

export default WrappedFormFieldReplaceVideo;
